import { Box, Card, CardActionArea, Stack, cardActionAreaClasses } from '@mui/material';
import { Position } from '@xyflow/react';
import { useMemo } from 'react';

import { api, colors, isDefined } from 'common';

import { NodeBody } from './Body';
import { NodeHandle } from './Handles/Handle';
import { NodeHeader } from './Header';
import { NodeHeaderLabel } from './NodeHeaderLabel';
import { BlockExecutionStatusOverlay } from './StatusOverlay';
import { HANDLE_TOP_OFFSET_IN_PIXELS, NODE_WIDTH_IN_PIXELS } from './cardDimensions';
import { type ReactFlowBlockNodeProps } from './types';
import { useCodeMirrorConfig } from '../../../../Code/codeMirrorHooks';
import { EdgeHandleType } from '../../../../types';
import { blockConfigsByBlockType } from '../../../blockConfigs';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockExecutionState } from '../../../types';
import { useBlockConfigValidation } from '../../ConfigFields/useBlockConfigValidation';
import {
  REACT_FLOW_CARD_BORDER_RADIUS,
  REACT_FLOW_CARD_DEFAULT_BOX_SHADOW,
  REACT_FLOW_CARD_GROUPED_BOX_SHADOW,
  REACT_FLOW_CARD_HIGHLIGHTED_BOX_SHADOW,
} from '../constants';
import { NodeCardDecorators } from './Decorators/Decorators';
import {
  EventAction,
  EventCategory,
  useCaptureEvents,
} from '../../../../../../../utils/events/index';
import { useFlowDefinitionContext } from '../../../../context';
import { getFlowDefinitionEventProperties } from '../../../../utils';
import { useReactFlowTransformState } from '../../../lib/useReactFlowTransformState';

const { IdentifierNameSchema, reservedBlockNames } = api.FlowDefinition;

function getProgressingFlowBlockColor(state: BlockExecutionState | undefined): string | undefined {
  switch (state) {
    case BlockExecutionState.Running:
      return colors.success.default;
    case BlockExecutionState.Waiting:
      return colors.extended.gray[75];
    case BlockExecutionState.PendingRetry:
      return colors.warning.default;
    default:
      return undefined;
  }
}

export function NodeCard(props: ReactFlowBlockNodeProps) {
  const {
    id: nodeId,
    selected: isReactFlowNodeSelected,
    data,
    parentId,
    data: { uiBlockType, label, description, config: nodeConfig },
  } = props;
  const blockConfig = blockConfigsByBlockType[uiBlockType];
  const { nodeHandles: nodeHandlesProp } = blockConfig;
  const { schema, getLanguageFieldValues } = blockConfig.configFields ?? {};
  const {
    isEditable,
    areBlockErrorsVisible,
    setSelectedNodeId,
    selectedNodeId,
    blockNodes,
    uiMode,
  } = useReactFlowViewContext();
  const { reactFlowZoomLevel } = useReactFlowTransformState();

  const inProgressColor = getProgressingFlowBlockColor(data.run?.state);
  const blockIsInProgress = isDefined(inProgressColor);
  const isSelected = nodeId === selectedNodeId || isReactFlowNodeSelected;
  const { validateFieldValueLanguages } = useCodeMirrorConfig({
    nodeId,
  });
  const { captureEvent } = useCaptureEvents();
  const { flowDefinition } = useFlowDefinitionContext();

  const isLabelValid = useMemo(() => {
    if (!areBlockErrorsVisible) {
      return true;
    }
    try {
      return IdentifierNameSchema.refine(
        (value) => !reservedBlockNames.includes(value),
        'Name cannot be a reserved word'
      )
        .refine(
          (value) => !blockNodes.some((node) => node.id !== nodeId && node.data.label === value),
          'Name must be unique'
        )
        .safeParse(label).success;
    } catch {
      return false;
    }
  }, [blockNodes, nodeId, label, areBlockErrorsVisible]);

  const { doesSchemaValidate } = useBlockConfigValidation({
    schema,
    nodeId,
    nodeConfig,
    nodeData: data,
    enabled: areBlockErrorsVisible,
  });
  const areFieldLanguagesValid = useMemo(() => {
    if (!areBlockErrorsVisible || !isDefined(getLanguageFieldValues) || !isDefined(nodeConfig)) {
      return true;
    }
    const languageFieldValues = getLanguageFieldValues(nodeConfig);
    return validateFieldValueLanguages(languageFieldValues);
  }, [nodeConfig, getLanguageFieldValues, validateFieldValueLanguages, areBlockErrorsVisible]);

  const cardIsValid = isLabelValid && doesSchemaValidate && areFieldLanguagesValid;
  const nodeHandles = useMemo(() => {
    if (typeof nodeHandlesProp === 'function') {
      return nodeHandlesProp(nodeConfig);
    }
    return nodeHandlesProp;
  }, [nodeHandlesProp, nodeConfig]);

  return (
    <Box
      position="relative"
      className={
        isEditable
          ? undefined
          : /**
             * This is only called if `nodesDraggable` is `undefined` in the React Flow component.
             * The `nodrag` class allows the user to click on the card even if their mouse moves slightly.
             * This is important because the read only nodes are hard to select if the node is draggable.
             */
            'nodrag'
      }
    >
      <Card
        variant="outlined"
        sx={{
          borderRadius: REACT_FLOW_CARD_BORDER_RADIUS,
          border: '1px solid',
          borderStyle: 'solid',
          borderColor: blockIsInProgress
            ? inProgressColor
            : cardIsValid
              ? isSelected
                ? colors.primary.default
                : '#e6e6e6'
              : 'error.main',
          opacity:
            data.run?.state === BlockExecutionState.Invalidated
              ? 0.5
              : data.run?.state === BlockExecutionState.Pending
                ? 0.76
                : undefined,
          backgroundColor:
            cardIsValid || reactFlowZoomLevel > 0.5 ? undefined : 'rgba(255, 0, 0, 0.12)',
          boxShadow: blockIsInProgress
            ? `0 0 4px 2px ${inProgressColor}`
            : isSelected
              ? REACT_FLOW_CARD_HIGHLIGHTED_BOX_SHADOW
              : isDefined(parentId)
                ? REACT_FLOW_CARD_GROUPED_BOX_SHADOW
                : REACT_FLOW_CARD_DEFAULT_BOX_SHADOW,
          transition: 'background-color 0.2s ease-in-out',
          '&:hover': {
            boxShadow: blockIsInProgress ? undefined : REACT_FLOW_CARD_HIGHLIGHTED_BOX_SHADOW,
          },
          /**
           * Our node handles extend outside of the card, so we need to allow the card to not hide overflow.
           * Overflow needs to be handled in each part of the node card.
           */
          overflow: 'unset',
        }}
      >
        <CardActionArea
          data-parent-id={parentId}
          onClick={() => {
            setSelectedNodeId(nodeId);
            captureEvent({
              category: EventCategory.FlowDefinitionEdit,
              action: EventAction.BlockClick,
              properties: {
                ...getFlowDefinitionEventProperties(flowDefinition),
                blockId: nodeId,
                blockType: uiBlockType,
                flowUiMode: uiMode,
              },
            });
          }}
          sx={{
            [`&:hover > .${cardActionAreaClasses.focusHighlight}`]: {
              // Hide the focus highlight overlay on node cards.
              opacity: 0,
            },
          }}
        >
          <Stack
            gap={1}
            sx={{
              width: NODE_WIDTH_IN_PIXELS,
              paddingY: 1,
            }}
          >
            <Stack marginX={2} gap={1}>
              <NodeHeader
                uiBlockType={uiBlockType}
                description={description}
                blockIconSubtypeVariant="large"
              >
                <NodeHeaderLabel data={props.data} isSelected={isSelected} />
              </NodeHeader>
              <NodeCardDecorators data={data} nodeId={nodeId} />
            </Stack>
            {nodeHandles
              ?.filter((handle) => !handle.id)
              .map((handle) => {
                const { type } = handle;
                return (
                  <NodeHandle
                    key={type}
                    type={type}
                    position={type === 'target' ? Position.Left : Position.Right}
                    style={{
                      /**
                       * This property is related to snap-to-grid behavior and placement of new nodes.
                       * @see NodeHandlePlacementUsage
                       */
                      top: HANDLE_TOP_OFFSET_IN_PIXELS,
                    }}
                    nodeProps={props}
                    edgeHandleType={EdgeHandleType.Default}
                  />
                );
              })}
            <NodeBody nodeProps={props} nodeHandles={nodeHandles} />
          </Stack>
        </CardActionArea>
      </Card>
      <BlockExecutionStatusOverlay
        blockExecutionState={data.run?.state}
        nodeId={nodeId}
        dataSourceType={data.run?.metadata?.source?.type}
      />
    </Box>
  );
}
